import React, { useState } from "react";
import { TOKENS } from "../tokens";
import { Card } from "../components/Card";
import { SectionLabel } from "../components/SectionLabel";
import { StatusPill } from "../components/StatusPill";
import { useApp } from "../context/AppContext";
import { getImageUrl } from "../api/client";
import { is18Plus, validatePhone, validateEmail } from "../utils/validation";
import { WhatsAppPhoneField } from "../components/WhatsAppPhoneField";
import { MapPin, ChevronRight, ChevronLeft, Search, FileText, Clock, Calendar, UserCheck, UserX, Navigation, ExternalLink } from "lucide-react";
import { getEmpStatus, isEmpAbsent, canMarkAbsent, isMarkedAbsentToday } from "../utils/empStatus";
import { fmtDate } from "../hooks/useClock";

// ── Attendance calendar helpers ───────────────────────────────────────────────
const ATT_COLORS = {
  present: TOKENS.gold,
  od:      TOKENS.blue,
  late:    TOKENS.warning,
  absent:  TOKENS.danger,
  leave:   TOKENS.navySoft,
  lop:     "#9A3412",
  off:     "#CCC",
};
const ATT_LABELS = {
  present: "Present", od: "On OD", late: "Late", absent: "Absent / LOP",
  leave: "Leave", lop: "LOP", off: "Weekly Holiday",
};
const MONTH_NAMES = ["January","February","March","April","May","June","July","August","September","October","November","December"];

const FILTERS = ["All", "Checked In", "On OD", "Daily Report", "Checked Out", "Absent"];

export function TeamScreen({ onSelectEmp }) {
  const { team, assignTask, addEmployee, markEmployeeAbsent, refreshTeam } = useApp();
  const [filter, setFilter] = useState("All");
  const [search, setSearch] = useState("");

  // ── View mode: "team" | "attendance" ─────────────────────────────────────
  const [viewMode, setViewMode] = useState("team");

  // ── Attendance calendar state ─────────────────────────────────────────────
  const getTodayStr = () => new Date().toLocaleDateString('en-CA', { timeZone: 'Asia/Kolkata' });
  const [attEmpId, setAttEmpId] = useState(null);
  const [attYear, setAttYear]   = useState(new Date().getFullYear());
  const [attMonth, setAttMonth] = useState(new Date().getMonth());
  const [attDate, setAttDate]   = useState(getTodayStr());

  const attEmp = attEmpId ? team.find(e => e.id === attEmpId) : null;

  const attPrevMonth = () => { if (attMonth === 0) { setAttMonth(11); setAttYear(y => y-1); } else setAttMonth(m => m-1); };
  const attNextMonth = () => { if (attMonth === 11) { setAttMonth(0); setAttYear(y => y+1); } else setAttMonth(m => m+1); };

  // Assign task modal states
  const [isAssignOpen, setIsAssignOpen] = useState(false);
  const [selectedEmpId, setSelectedEmpId] = useState("");
  const [taskTitle, setTaskTitle] = useState("");
  const [taskCategory, setTaskCategory] = useState("Calibration");
  const [taskLocation, setTaskLocation] = useState("");
  const [clientRef, setClientRef] = useState("");
  const [taskClientName, setTaskClientName] = useState("");
  const [taskProjectName, setTaskProjectName] = useState("");
  const [loading, setLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");

  // Add Employee modal states
  const [isAddEmpOpen, setIsAddEmpOpen] = useState(false);
  const [newEmpId, setNewEmpId] = useState("");
  const [newName, setNewName] = useState("");
  const [newRole, setNewRole] = useState("");
  const [newFatherName, setNewFatherName] = useState("");
  const [newMotherName, setNewMotherName] = useState("");
  const [newDob, setNewDob] = useState("");
  const [newBloodGroup, setNewBloodGroup] = useState("B+");
  const [newPhone, setNewPhone] = useState("");
  const [newEmail, setNewEmail] = useState("");
  const [newEmergencyName, setNewEmergencyName] = useState("");
  const [newEmergencyRelationship, setNewEmergencyRelationship] = useState("Father");
  const [newEmergencyPhone, setNewEmergencyPhone] = useState("");
  const [newClient, setNewClient] = useState("");
  const [newTeam, setNewTeam] = useState("");
  const [newJoiningDate, setNewJoiningDate] = useState(new Date().toISOString().slice(0, 10));
  const [newPassword, setNewPassword] = useState("");
  const [newHasWhatsapp, setNewHasWhatsapp] = useState(false);
  const [waCheckStatus, setWaCheckStatus] = useState("idle"); // idle | checking | yes | no
  const [loadingAdd, setLoadingAdd] = useState(false);
  const [errorAdd, setErrorAdd] = useState("");
  const [absentingId, setAbsentingId] = useState(null);

  const handleMarkAbsent = async (emp, e) => {
    if (e) e.stopPropagation();
    if (!canMarkAbsent(emp)) return;
    const id = emp.employee_id || emp.employeeId || emp.id;
    if (!confirm(`Mark ${emp.name} as Absent for today?`)) return;
    setAbsentingId(id);
    try {
      await markEmployeeAbsent(id);
    } catch (err) {
      alert(err.response?.data?.error || `Failed to mark ${emp.name} absent.`);
    } finally {
      setAbsentingId(null);
      if (refreshTeam) refreshTeam();
    }
  };

  const handleOpenAddEmpModal = () => {
    setIsAddEmpOpen(true);
    setNewEmpId("");
    setNewName("");
    setNewRole("");
    setNewFatherName("");
    setNewMotherName("");
    setNewDob("");
    setNewBloodGroup("B+");
    setNewPhone("");
    setNewEmail("");
    setNewEmergencyName("");
    setNewEmergencyRelationship("Father");
    setNewEmergencyPhone("");
    setNewClient("");
    setNewTeam("");
    setNewJoiningDate(new Date().toISOString().slice(0, 10));
    setNewPassword("");
    setNewHasWhatsapp(false);
    setWaCheckStatus("idle");
    setErrorAdd("");
  };

  const handleAddEmployee = async (e) => {
    e.preventDefault();
    if (!newEmpId.trim() || !newName.trim() || !newRole.trim() || !newPassword.trim()) {
      setErrorAdd("Employee ID, Name, Role, and Password are required.");
      return;
    }
    if (newDob && !is18Plus(newDob)) {
      setErrorAdd("Date of Birth must indicate age 18 or older.");
      return;
    }
    if (newPhone && !validatePhone(newPhone)) {
      setErrorAdd("Phone number must be exactly 10 digits.");
      return;
    }
    if (newEmail && !validateEmail(newEmail)) {
      setErrorAdd("Email address must be valid and end with .com (e.g. name@gmail.com).");
      return;
    }
    if (newPhone && waCheckStatus === "checking") {
      setErrorAdd("Still checking whether this mobile has WhatsApp. Please wait a moment.");
      return;
    }
    if (newPhone && waCheckStatus === "no") {
      if (!window.confirm("This number does not appear to have WhatsApp (No WA). Save anyway? Daily status will not be sent to this mobile.")) {
        return;
      }
    }

    setErrorAdd("");
    setLoadingAdd(true);
    try {
      await addEmployee({
        employeeId: newEmpId.trim(),
        name: newName.trim(),
        role: newRole.trim(),
        fatherName: newFatherName.trim(),
        motherName: newMotherName.trim(),
        dob: newDob,
        bloodGroup: newBloodGroup,
        phone: newPhone.trim(),
        email: newEmail.trim(),
        emergencyName: newEmergencyName.trim(),
        emergencyRelationship: newEmergencyRelationship.trim(),
        emergencyPhone: newEmergencyPhone.trim(),
        clientName: newClient.trim(),
        teamName: newTeam.trim(),
        joiningDate: newJoiningDate,
        password: newPassword.trim(),
        hasWhatsapp: newHasWhatsapp,
      });
      setIsAddEmpOpen(false);
    } catch (err) {
      setErrorAdd(err.response?.data?.error || "Failed to add employee.");
    } finally {
      setLoadingAdd(false);
    }
  };

  const handleOpenAssignModal = () => {
    setIsAssignOpen(true);
    setSelectedEmpId(team[0]?.employee_id || "");
    setTaskTitle("");
    setTaskCategory("Calibration");
    setTaskLocation("");
    setTaskClientName("");
    setTaskProjectName("");
    setClientRef(`REF-${Date.now().toString().slice(-4)}`);
    setErrorMsg("");
  };

  const handleAssignTask = async (e) => {
    e.preventDefault();
    if (!taskTitle.trim() || !taskLocation.trim()) {
      setErrorMsg("Please fill in both Task Title and Location.");
      return;
    }
    setErrorMsg("");
    setLoading(true);
    try {
      await assignTask({
        employeeId: selectedEmpId,
        title: taskTitle.trim(),
        category: taskCategory,
        location: taskLocation.trim(),
        clientRef: clientRef.trim(),
        clientName: taskClientName.trim(),
        projectName: taskProjectName.trim(),
      });
      setIsAssignOpen(false);
    } catch (err) {
      setErrorMsg("Failed to assign task. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const today = new Date().toLocaleDateString('en-CA', { timeZone: 'Asia/Kolkata' });
  const filtered = team.filter((emp) => {
    const matchSearch = emp.name.toLowerCase().includes(search.toLowerCase()) ||
      (emp.lastLocation || "").toLowerCase().includes(search.toLowerCase());
    if (!matchSearch) return false;
    if (filter === "All") return true;
    if (filter === "Checked In") return emp.checkedIn && !emp.onOD;
    if (filter === "On OD") return emp.onOD;
    if (filter === "Daily Report") return emp.hasSubmittedReportToday || (emp.reports && emp.reports.some(r => r.date === today));
    if (filter === "Checked Out") return !emp.checkedIn && !emp.onOD && !!emp.checkOutTime;
    if (filter === "Absent") return isEmpAbsent(emp);
    return true;
  }).sort((a, b) => b.score - a.score);

  if (viewMode === "attendance" && attEmp) {
    const attendance = attEmp.attendance || {};
    const checkinsHistory = attEmp.checkinsHistory || [];
    const selectedCheckins = checkinsHistory.filter(c => c.date === attDate).sort((a,b) => a.id - b.id);
    const checkInRecord = selectedCheckins.find(c => c.type === 'in');
    const checkOutRecord = selectedCheckins.slice().reverse().find(c => c.type === 'out');
    const isSelectedDateSunday = (() => {
      const [y, m, d] = attDate.split("-").map(Number);
      return new Date(y, m - 1, d).getDay() === 0;
    })();
    const selectedStatus = attendance[attDate] || (attDate > getTodayStr() || isSelectedDateSunday ? "off" : "absent");
    const daysInMonth = new Date(attYear, attMonth + 1, 0).getDate();
    const firstDayOfWeek = new Date(attYear, attMonth, 1).getDay();

    const legend = [
      { label: "Present", color: TOKENS.gold },
      { label: "On OD",   color: TOKENS.blue },
      { label: "Leave",   color: TOKENS.navySoft },
      { label: "LOP",     color: "#9A3412" },
      { label: "Late",    color: TOKENS.warning },
      { label: "Absent",  color: TOKENS.danger },
    ];

    return (
      <div>
        <button onClick={() => setAttEmpId(null)} style={{
          display: "flex", alignItems: "center", gap: 6, border: "none",
          background: "none", color: TOKENS.navyDeep, cursor: "pointer",
          marginBottom: 14, fontWeight: 700, fontSize: 13,
        }}>
          ← Back to All Employees
        </button>

        <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 20 }}>
          <div style={{
            width: 44, height: 44, borderRadius: "50%", background: TOKENS.navyDeep,
            color: TOKENS.gold, display: "flex", alignItems: "center", justifyContent: "center",
            fontSize: 18, fontWeight: 700
          }}>
            {attEmp.initials || "EM"}
          </div>
          <div>
            <h2 style={{ margin: 0, fontSize: 18, color: TOKENS.navyDeep }}>{attEmp.name}</h2>
            <div style={{ fontSize: 12, color: TOKENS.muted }}>{attEmp.role}</div>
          </div>
        </div>

        {/* Legend */}
        <div style={{ display: "flex", gap: 12, marginBottom: 14, flexWrap: "wrap" }}>
          {legend.map(({ label, color }) => (
            <div key={label} style={{ display: "flex", alignItems: "center", gap: 5 }}>
              <div style={{ width: 10, height: 10, borderRadius: "50%", background: color }} />
              <span style={{ fontSize: 11, color: TOKENS.muted, fontWeight: 600 }}>{label}</span>
            </div>
          ))}
        </div>

        {/* Calendar Section */}
        <Card style={{ padding: "14px 12px", marginBottom: 24, overflowX: "auto" }}>
          {/* Month Selector Header */}
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 14, flexWrap: "wrap", gap: 10 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <span style={{ fontFamily: "Fraunces, serif", fontSize: 18, fontWeight: 700, color: TOKENS.navyDeep }}>
                {MONTH_NAMES[attMonth]} {attYear}
              </span>
            </div>

            <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
              <button onClick={attPrevMonth} style={{
                background: TOKENS.cream, border: `1px solid ${TOKENS.border}`,
                borderRadius: 8, padding: "6px 10px", cursor: "pointer", display: "flex", alignItems: "center"
              }}>
                <ChevronLeft size={18} color={TOKENS.navyDeep} />
              </button>
              <button onClick={() => {
                const now = new Date();
                setAttYear(now.getFullYear());
                setAttMonth(now.getMonth());
                setAttDate(getTodayStr());
              }} style={{
                background: TOKENS.cream, border: `1px solid ${TOKENS.border}`,
                borderRadius: 8, padding: "6px 12px", fontSize: 12, fontWeight: 700, color: TOKENS.navyDeep, cursor: "pointer"
              }}>
                Today
              </button>
              <button onClick={attNextMonth} style={{
                background: TOKENS.cream, border: `1px solid ${TOKENS.border}`,
                borderRadius: 8, padding: "6px 10px", cursor: "pointer", display: "flex", alignItems: "center"
              }}>
                <ChevronRight size={18} color={TOKENS.navyDeep} />
              </button>
            </div>
          </div>

          <div style={{ display: "grid", gridTemplateColumns: "repeat(7, minmax(0, 1fr))", gap: 4, width: "100%" }}>
            {["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map(day => (
              <div key={day} style={{ textAlign: "center", fontSize: 10, fontWeight: 700, color: TOKENS.muted, padding: "4px 0" }}>{day}</div>
            ))}
            
            {Array.from({ length: firstDayOfWeek }).map((_, i) => (
              <div key={`empty-${i}`} style={{ minHeight: 44, background: "transparent" }} />
            ))}

            {Array.from({ length: daysInMonth }).map((_, i) => {
              const dayNum = i + 1;
              const dateStr = `${attYear}-${String(attMonth + 1).padStart(2, "0")}-${String(dayNum).padStart(2, "0")}`;
              const isSelected = dateStr === attDate;
              const isToday = dateStr === getTodayStr();
              const isFuture = dateStr > getTodayStr();
              
              const isSunday = new Date(attYear, attMonth, dayNum).getDay() === 0;
              const status = isFuture ? "off" : (attendance[dateStr] || (isSunday ? "off" : "absent"));

              return (
                <div
                  key={dateStr}
                  onClick={() => !isFuture && setAttDate(dateStr)}
                  style={{
                    minHeight: 46, borderRadius: 8, padding: "4px 4px", cursor: isFuture ? "not-allowed" : "pointer",
                    display: "flex", flexDirection: "column", justifyContent: "space-between", alignItems: "center",
                    border: isSelected ? `2px solid ${TOKENS.navyDeep}` : isToday ? `1.5px solid ${TOKENS.gold}` : `1px solid ${TOKENS.border}`,
                    background: isSelected ? `${TOKENS.navyDeep}10` : isToday ? "rgba(10,25,50,0.03)" : "#fff",
                    transition: "all 0.15s ease", opacity: isFuture ? 0.5 : 1
                  }}
                >
                  <span style={{ fontSize: 11.5, fontWeight: isSelected || isToday ? 800 : 600, color: isSelected ? TOKENS.navyDeep : TOKENS.ink }}>
                    {dayNum}
                  </span>
                  {!isFuture && (
                    <div style={{
                      width: 12, height: 12, borderRadius: "50%",
                      background: ATT_COLORS[status] || "#EEE",
                      marginTop: 4
                    }} title={ATT_LABELS[status]} />
                  )}
                </div>
              );
            })}
          </div>
        </Card>

        {/* Selected Date Details */}
        <SectionLabel>Details for {attDate}</SectionLabel>
        <Card style={{ padding: 16 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 16 }}>
            <div style={{ padding: "4px 10px", borderRadius: 8, fontSize: 12, fontWeight: 700, color: "#fff", background: ATT_COLORS[selectedStatus] || "#999" }}>
              {ATT_LABELS[selectedStatus]}
            </div>
          </div>

          <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
            <div style={{ display: "flex", alignItems: "flex-start", gap: 12, background: TOKENS.cream, padding: 12, borderRadius: 10, border: `1px solid ${TOKENS.border}` }}>
              <div style={{ width: 36, height: 36, borderRadius: 10, background: `${TOKENS.success}20`, color: TOKENS.success, display: "flex", alignItems: "center", justifyContent: "center", marginTop: 4, flexShrink: 0 }}>
                <Clock size={18} />
              </div>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 11, color: TOKENS.muted, fontWeight: 700, textTransform: "uppercase" }}>Check In</div>
                {checkInRecord ? (
                  <>
                    <div style={{ fontSize: 15, fontWeight: 800, color: TOKENS.navyDeep, margin: "2px 0" }}>
                      {checkInRecord.timestamp} · <span style={{ fontSize: 13, fontWeight: 600, color: TOKENS.navySoft }}>{fmtDate(checkInRecord.date)}</span>
                    </div>
                    <div style={{ fontSize: 11.5, color: TOKENS.navySoft, marginTop: 4, display: "flex", flexDirection: "column", gap: 3 }}>
                      <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
                        <MapPin size={12} color={TOKENS.navySoft} />
                        <span><b>Location:</b> {checkInRecord.city || "Unknown Location"}</span>
                      </div>
                      {checkInRecord.lat && (
                        <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
                          <Navigation size={12} color={TOKENS.navySoft} />
                          <span>
                            <b>GPS Coords:</b> {checkInRecord.lat.toFixed(6)}° N, {checkInRecord.lng.toFixed(6)}° E 
                            {checkInRecord.accuracy ? ` (±${checkInRecord.accuracy}m)` : ""} · 
                            <span style={{ color: checkInRecord.isRealGps ? TOKENS.success : TOKENS.warning, fontWeight: 700 }}>
                              {checkInRecord.isRealGps ? " Live GPS" : " Network"}
                            </span>
                          </span>
                        </div>
                      )}
                    </div>
                    {checkInRecord.lat && (
                      <a
                        href={`https://www.google.com/maps?q=${checkInRecord.lat},${checkInRecord.lng}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{
                          display: "inline-flex", alignItems: "center", gap: 4, marginTop: 8,
                          fontSize: 11, fontWeight: 700, color: TOKENS.blue, textDecoration: "none",
                          background: `${TOKENS.blue}15`, padding: "4px 8px", borderRadius: 6,
                        }}
                      >
                        <ExternalLink size={12} /> View Map
                      </a>
                    )}
                  </>
                ) : (
                  <div style={{ fontSize: 14, color: TOKENS.muted, fontStyle: "italic", marginTop: 2 }}>Not checked in on this date</div>
                )}
              </div>
            </div>

            <div style={{ display: "flex", alignItems: "flex-start", gap: 12, background: TOKENS.cream, padding: 12, borderRadius: 10, border: `1px solid ${TOKENS.border}` }}>
              <div style={{ width: 36, height: 36, borderRadius: 10, background: `${TOKENS.danger}20`, color: TOKENS.danger, display: "flex", alignItems: "center", justifyContent: "center", marginTop: 4, flexShrink: 0 }}>
                <Clock size={18} />
              </div>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 11, color: TOKENS.muted, fontWeight: 700, textTransform: "uppercase" }}>Check Out</div>
                {checkOutRecord ? (
                  <>
                    <div style={{ fontSize: 15, fontWeight: 800, color: TOKENS.navyDeep, margin: "2px 0" }}>
                      {checkOutRecord.timestamp} · <span style={{ fontSize: 13, fontWeight: 600, color: TOKENS.navySoft }}>{fmtDate(checkOutRecord.date)}</span>
                    </div>
                    <div style={{ fontSize: 11.5, color: TOKENS.navySoft, marginTop: 4, display: "flex", flexDirection: "column", gap: 3 }}>
                      <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
                        <MapPin size={12} color={TOKENS.navySoft} />
                        <span><b>Location:</b> {checkOutRecord.city || "Unknown Location"}</span>
                      </div>
                      {checkOutRecord.lat && (
                        <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
                          <Navigation size={12} color={TOKENS.navySoft} />
                          <span>
                            <b>GPS Coords:</b> {checkOutRecord.lat.toFixed(6)}° N, {checkOutRecord.lng.toFixed(6)}° E 
                            {checkOutRecord.accuracy ? ` (±${checkOutRecord.accuracy}m)` : ""} · 
                            <span style={{ color: checkOutRecord.isRealGps ? TOKENS.success : TOKENS.warning, fontWeight: 700 }}>
                              {checkOutRecord.isRealGps ? " Live GPS" : " Network"}
                            </span>
                          </span>
                        </div>
                      )}
                    </div>
                    {checkOutRecord.lat && (
                      <a
                        href={`https://www.google.com/maps?q=${checkOutRecord.lat},${checkOutRecord.lng}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{
                          display: "inline-flex", alignItems: "center", gap: 4, marginTop: 8,
                          fontSize: 11, fontWeight: 700, color: TOKENS.blue, textDecoration: "none",
                          background: `${TOKENS.blue}15`, padding: "4px 8px", borderRadius: 6,
                        }}
                      >
                        <ExternalLink size={12} /> View Map
                      </a>
                    )}
                  </>
                ) : (
                  <div style={{ fontSize: 14, color: TOKENS.muted, fontStyle: "italic", marginTop: 2 }}>Not checked out on this date</div>
                )}
              </div>
            </div>
          </div>
        </Card>
      </div>
    );
  }

  // ── Status helpers ───────────────────────────────────────────────────────
  const statusConfig = {
    "Checked In":  { bg: "#E7F5EF", color: TOKENS.success,  dot: TOKENS.success,  label: "Checked In" },
    "On OD":       { bg: "#EFF6FF", color: TOKENS.blue,     dot: TOKENS.blue,     label: "On OD"      },
    "Checked Out": { bg: "#F5EDCC", color: TOKENS.warning,  dot: TOKENS.warning,  label: "Checked Out"},
    "Absent":      { bg: "#FBEFE9", color: TOKENS.danger,   dot: TOKENS.danger,   label: "Absent / LOP" },
    "Pending":     { bg: "#F3F4F6", color: TOKENS.muted,    dot: TOKENS.muted,    label: "Pending"    },
    present:       { bg: "#E7F5EF", color: TOKENS.success,  dot: TOKENS.success,  label: "Checked In" },
    od:            { bg: "#EFF6FF", color: TOKENS.blue,     dot: TOKENS.blue,     label: "On OD"      },
    "checked-out": { bg: "#F5EDCC", color: TOKENS.warning,  dot: TOKENS.warning,  label: "Checked Out"},
    absent:        { bg: "#FBEFE9", color: TOKENS.danger,   dot: TOKENS.danger,   label: "Absent / LOP" },
    leave:         { bg: "#EFF6FF", color: TOKENS.navyDeep, dot: TOKENS.navySoft, label: "Leave"      },
    lop:           { bg: TOKENS.warningBg, color: TOKENS.warning, dot: TOKENS.warning, label: "LOP" },
    "pending-arrival": { bg: "#F3F4F6", color: TOKENS.muted, dot: TOKENS.muted,  label: "Pending"    },
  };

  const checkedInCount  = team.filter(e => e.checkedIn && !e.onOD).length;
  const onODCount       = team.filter(e => e.onOD).length;
  const absentCount     = team.filter(isEmpAbsent).length;
  const checkedOutCount = team.filter(e => !e.checkedIn && !e.onOD && !!e.checkOutTime).length;

  return (
    <div>
      {/* ── View Toggle ─────────────────────────────────────────────────────── */}
      <div style={{
        display: "inline-flex", background: TOKENS.navyDeep, borderRadius: 14,
        padding: 4, marginBottom: 20, gap: 3,
        boxShadow: "0 4px 18px rgba(10,31,58,0.18)",
      }}>
        {[
          { key: "team",       label: "👥  Team View"  },
          { key: "attendance", label: "📅  Attendance" },
        ].map(({ key, label }) => (
          <button
            key={key}
            onClick={() => { setViewMode(key); setAttEmpId(null); }}
            style={{
              padding: "9px 22px", border: "none", borderRadius: 11,
              fontSize: 13, fontWeight: 700, cursor: "pointer",
              transition: "all 0.2s ease",
              background: viewMode === key ? TOKENS.gold : "transparent",
              color:      viewMode === key ? TOKENS.navyDeep : "rgba(255,255,255,0.55)",
            }}
          >
            {label}
          </button>
        ))}
      </div>

      {/* ── Header ──────────────────────────────────────────────────────────── */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 18, flexWrap: "wrap", gap: 12 }}>
        <div>
          <h2 style={{ fontFamily: "Fraunces, serif", fontSize: 26, fontWeight: 700, color: TOKENS.navyDeep, margin: 0, lineHeight: 1.1 }}>
            {viewMode === "team" ? "Team Overview" : "Attendance Tracker"}
          </h2>
          <div style={{ fontSize: 12.5, color: TOKENS.muted, marginTop: 3, fontWeight: 500 }}>
            {viewMode === "team" ? `Total Employees Employed: ${team.length} · ranked by performance score` : `Select an employee to view attendance history`}
          </div>
        </div>
        <div style={{ display: "flex", gap: 8 }}>
          <button
            onClick={handleOpenAddEmpModal}
            style={{
              background: "#fff", color: TOKENS.navyDeep, border: `1.5px solid ${TOKENS.navyDeep}`,
              borderRadius: 12, padding: "9px 16px", fontWeight: 700,
              fontSize: 13, cursor: "pointer", display: "flex", alignItems: "center", gap: 6,
            }}
          >
            + Add Employee
          </button>
          <button
            onClick={handleOpenAssignModal}
            style={{
              background: TOKENS.navyDeep, color: TOKENS.gold, border: "none",
              borderRadius: 12, padding: "10px 18px", fontWeight: 700,
              fontSize: 13, cursor: "pointer", display: "flex", alignItems: "center", gap: 6,
              boxShadow: `0 4px 14px ${TOKENS.navyDeep}33`,
            }}
          >
            ✦ Assign Task
          </button>
        </div>
      </div>

      {/* ── Live Stats Bar ───────────────────────────────────────────────────── */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 10, marginBottom: 20 }}>
        {[
          { label: "Checked In",  value: checkedInCount,  icon: "✅", bg: "#E7F5EF", color: TOKENS.success },
          { label: "On OD",       value: onODCount,        icon: "🚗", bg: "#EFF6FF", color: TOKENS.blue   },
          { label: "Checked Out", value: checkedOutCount,  icon: "🏁", bg: "#FEF3E2", color: TOKENS.warning },
          { label: "Absent",      value: absentCount,      icon: "⚠️", bg: "#FBEFE9", color: TOKENS.danger  },
        ].map(({ label, value, icon, bg, color }) => (
          <div key={label} style={{
            background: bg, borderRadius: 14, padding: "12px 14px",
            border: `1.5px solid ${color}22`, textAlign: "center",
          }}>
            <div style={{ fontSize: 20, marginBottom: 4 }}>{icon}</div>
            <div style={{ fontSize: 22, fontWeight: 800, color, fontFamily: "Fraunces, serif" }}>{value}</div>
            <div style={{ fontSize: 10.5, fontWeight: 700, color, opacity: 0.75, marginTop: 1 }}>{label}</div>
          </div>
        ))}
      </div>

      {/* ── Search ──────────────────────────────────────────────────────────── */}
      <div style={{ position: "relative", marginBottom: 14 }}>
        <Search size={15} color={TOKENS.muted} style={{ position: "absolute", left: 13, top: "50%", transform: "translateY(-50%)" }} />
        <input
          placeholder="Search by name or city…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          style={{
            width: "100%", padding: "12px 12px 12px 40px", borderRadius: 13,
            border: `1.5px solid ${TOKENS.border}`, fontSize: 13.5, outline: "none",
            background: "#fff", color: TOKENS.ink, boxSizing: "border-box",
            boxShadow: "0 2px 8px rgba(0,0,0,0.04)",
          }}
        />
      </div>

      {/* ── Filter Chips ────────────────────────────────────────────────────── */}
      <div style={{ display: "flex", gap: 7, marginBottom: 20, overflowX: "auto", paddingBottom: 4 }}>
        {FILTERS.map((f) => (
          <button key={f} onClick={() => setFilter(f)} style={{
            padding: "6px 15px", borderRadius: 20, whiteSpace: "nowrap",
            background: filter === f ? TOKENS.navyDeep : "#fff",
            color:      filter === f ? "#fff" : TOKENS.muted,
            fontSize: 12, fontWeight: 700, cursor: "pointer",
            border: `1.5px solid ${filter === f ? TOKENS.navyDeep : TOKENS.border}`,
            transition: "all 0.15s ease",
          }}>{f}</button>
        ))}
        <span style={{ marginLeft: "auto", fontSize: 12, color: TOKENS.muted, fontWeight: 600, whiteSpace: "nowrap", alignSelf: "center", paddingRight: 4 }}>
          {filtered.length} employees
        </span>
      </div>

      {/* ── Empty State ─────────────────────────────────────────────────────── */}
      {filtered.length === 0 ? (
        <div style={{ padding: "40px 24px", textAlign: "center", background: "#FAFBFD", borderRadius: 18, border: `2px dashed ${TOKENS.border}` }}>
          <div style={{ fontSize: 36, marginBottom: 12 }}>👥</div>
          <div style={{ fontSize: 15, fontWeight: 700, color: TOKENS.navyDeep, marginBottom: 6 }}>No Employees Found</div>
          <div style={{ fontSize: 12.5, color: TOKENS.muted, marginBottom: 18 }}>Click "Add Employee" to create a new employee profile.</div>
          <button onClick={handleOpenAddEmpModal} style={{
            background: TOKENS.navyDeep, color: "#fff", border: "none",
            borderRadius: 10, padding: "9px 18px", fontWeight: 700, fontSize: 12.5, cursor: "pointer",
          }}>+ Add Employee Now</button>
        </div>

      ) : viewMode === "team" ? (
        /* ════════════════════════════════════════════════════════════════════
           TEAM VIEW — premium horizontal cards with status ring + score
        ════════════════════════════════════════════════════════════════════ */
        <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
          {filtered.map((emp, rank) => {
            const { status, checkOutTime } = getEmpStatus(emp);
            const cfg = statusConfig[status] || statusConfig["Pending"];
            const avatarBorder = emp.onOD ? TOKENS.blue : emp.checkedIn ? TOKENS.success : emp.checkOutTime ? TOKENS.warning : TOKENS.danger;

            return (
              <div
                key={emp.id}
                onClick={() => onSelectEmp(emp.id)}
                style={{
                  background: "#fff", borderRadius: 16, padding: "14px 16px",
                  border: `1.5px solid ${TOKENS.border}`, cursor: "pointer",
                  boxShadow: "0 2px 10px rgba(0,0,0,0.04)",
                  transition: "box-shadow 0.2s, transform 0.15s",
                  display: "flex", alignItems: "center", gap: 14,
                }}
                onMouseEnter={e => { e.currentTarget.style.boxShadow = "0 6px 24px rgba(10,31,58,0.11)"; e.currentTarget.style.transform = "translateY(-1px)"; }}
                onMouseLeave={e => { e.currentTarget.style.boxShadow = "0 2px 10px rgba(0,0,0,0.04)";   e.currentTarget.style.transform = "translateY(0)";    }}
              >
                {/* Rank badge */}
                <div style={{
                  width: 22, textAlign: "center", flexShrink: 0,
                  fontFamily: "Fraunces, serif", fontWeight: 800, fontSize: 15,
                  color: rank === 0 ? TOKENS.gold : rank === 1 ? "#A0A0A0" : rank === 2 ? "#CD7F32" : TOKENS.border,
                }}>
                  {rank + 1}
                </div>

                {/* Avatar with colored ring */}
                <div style={{ position: "relative", flexShrink: 0 }}>
                  <div style={{
                    width: 46, height: 46, borderRadius: "50%",
                    background: TOKENS.navyMid, color: "#fff",
                    display: "flex", alignItems: "center", justifyContent: "center",
                    fontSize: 14, fontWeight: 700,
                    border: `3px solid ${avatarBorder}`,
                    overflow: "hidden",
                  }}>
                    {emp.selfie
                      ? <img src={getImageUrl(emp.selfie)} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                      : emp.initials
                    }
                  </div>
                  {/* Status dot */}
                  <div style={{
                    position: "absolute", bottom: 0, right: 0,
                    width: 12, height: 12, borderRadius: "50%",
                    background: cfg.dot, border: "2px solid #fff",
                  }} />
                </div>

                {/* Main info */}
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 8 }}>
                    <div>
                      <div style={{ fontSize: 14, fontWeight: 700, color: TOKENS.ink, marginBottom: 1 }}>{emp.name}</div>
                      <div style={{ fontSize: 11.5, color: TOKENS.muted, fontWeight: 500 }}>{emp.role}</div>
                    </div>
                    <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 5, flexShrink: 0 }}>
                      <div style={{ fontFamily: "Fraunces, serif", fontSize: 18, fontWeight: 800, color: TOKENS.navyDeep, lineHeight: 1 }}>
                        {emp.score}
                        <span style={{ fontSize: 10, fontWeight: 600, color: TOKENS.muted, marginLeft: 2 }}>pts</span>
                      </div>
                      <div style={{
                        padding: "3px 9px", borderRadius: 20, fontSize: 10.5, fontWeight: 700,
                        background: cfg.bg, color: cfg.color,
                        whiteSpace: "nowrap",
                      }}>
                        {cfg.label}
                      </div>
                      {canMarkAbsent(emp) && !isMarkedAbsentToday(emp) && (
                        <button
                          onClick={(e) => handleMarkAbsent(emp, e)}
                          disabled={absentingId === (emp.employee_id || emp.id)}
                          style={{
                            padding: "3px 9px", borderRadius: 20, fontSize: 10, fontWeight: 700,
                            background: TOKENS.dangerBg, color: TOKENS.danger,
                            border: `1px solid ${TOKENS.danger}33`, cursor: "pointer",
                            display: "inline-flex", alignItems: "center", gap: 4,
                          }}
                        >
                          <UserX size={11} /> {absentingId === (emp.employee_id || emp.id) ? "Saving…" : "Mark Absent"}
                        </button>
                      )}
                    </div>
                  </div>

                  {/* Location + quick stats */}
                  <div style={{ display: "flex", alignItems: "center", gap: 10, marginTop: 8, flexWrap: "wrap" }}>
                    {emp.lastLocation && (
                      <span style={{ display: "flex", alignItems: "center", gap: 3, fontSize: 11, color: TOKENS.muted }}>
                        <MapPin size={10} color={TOKENS.muted} /> {emp.lastLocation}
                      </span>
                    )}
                    {(emp.checkInTime || emp.lastSeen) && (
                      <span style={{ display: "flex", alignItems: "center", gap: 3, fontSize: 11, color: TOKENS.muted }}>
                        <Clock size={10} color={TOKENS.muted} /> In: {emp.checkInTime || emp.lastSeen}
                      </span>
                    )}
                    {emp.checkOutTime && (
                      <span style={{ display: "flex", alignItems: "center", gap: 3, fontSize: 11, color: TOKENS.muted }}>
                        <Clock size={10} color={TOKENS.muted} /> Out: {emp.checkOutTime}
                      </span>
                    )}
                    {(emp.checkInLocation?.lat || emp.check_in_lat) && (
                      <a
                        href={`https://www.google.com/maps?q=${emp.checkInLocation?.lat || emp.check_in_lat},${emp.checkInLocation?.lng || emp.check_in_lng}`}
                        target="_blank" rel="noopener noreferrer"
                        onClick={e => e.stopPropagation()}
                        style={{ fontSize: 10.5, fontWeight: 700, color: TOKENS.blue, textDecoration: "none", background: `${TOKENS.blue}15`, padding: "2px 7px", borderRadius: 6 }}
                      >
                        🗺️ Map
                      </a>
                    )}
                    <div style={{ display: "flex", gap: 8, marginLeft: "auto" }}>
                      <span style={{ fontSize: 11, color: TOKENS.muted }}>
                        Tasks: <strong style={{ color: TOKENS.ink }}>{emp.tasksToday?.done || 0}/{emp.tasksToday?.total || 0}</strong>
                      </span>
                      <span style={{ fontSize: 11, color: TOKENS.muted }}>
                        🔥 <strong style={{ color: TOKENS.ink }}>{emp.streak || 0}d</strong>
                      </span>
                    </div>
                  </div>


                </div>

                <ChevronRight size={16} color={TOKENS.border} style={{ flexShrink: 0 }} />
              </div>
            );
          })}
        </div>

      ) : (
        /* ════════════════════════════════════════════════════════════════════
           ATTENDANCE VIEW — pick agent → show timeline
        ════════════════════════════════════════════════════════════════════ */
        <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
          {filtered.map((emp) => {
            const todayCheckins = (emp.checkinsHistory || []).filter(c => c.date === today);
            const checkInRec  = todayCheckins.find(c => c.type === "in");
            const checkOutRec = [...todayCheckins].reverse().find(c => c.type === "out");
            const { status } = getEmpStatus(emp);
            const cfg = statusConfig[status] || statusConfig["Pending"];
            const todayAttStatus = (emp.attendance || {})[today];
            const attColor = ATT_COLORS[todayAttStatus] || ATT_COLORS.absent;

            return (
              <div
                key={emp.id}
                onClick={() => setAttEmpId(emp.id)}
                style={{
                  background: "#fff", borderRadius: 16, padding: "14px 16px",
                  border: `1.5px solid ${TOKENS.border}`, cursor: "pointer",
                  boxShadow: "0 2px 10px rgba(0,0,0,0.04)",
                  transition: "box-shadow 0.2s, transform 0.15s",
                }}
                onMouseEnter={e => { e.currentTarget.style.boxShadow = "0 6px 24px rgba(10,31,58,0.11)"; e.currentTarget.style.transform = "translateY(-1px)"; }}
                onMouseLeave={e => { e.currentTarget.style.boxShadow = "0 2px 10px rgba(0,0,0,0.04)"; e.currentTarget.style.transform = "translateY(0)"; }}
              >
                <div style={{ display: "flex", alignItems: "center", gap: 13 }}>
                  {/* Avatar */}
                  <div style={{
                    width: 44, height: 44, borderRadius: "50%",
                    background: TOKENS.navyMid, color: "#fff",
                    display: "flex", alignItems: "center", justifyContent: "center",
                    fontSize: 14, fontWeight: 700, flexShrink: 0,
                    border: `3px solid ${attColor}`,
                    overflow: "hidden",
                  }}>
                    {emp.selfie
                      ? <img src={getImageUrl(emp.selfie)} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                      : emp.initials
                    }
                  </div>

                  {/* Name + role */}
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 14, fontWeight: 700, color: TOKENS.ink }}>{emp.name}</div>
                    <div style={{ fontSize: 11.5, color: TOKENS.muted }}>{emp.role}</div>
                  </div>

                  {/* Today's status pill */}
                  <div style={{ display: "flex", alignItems: "center", gap: 8, flexShrink: 0 }}>
                    {canMarkAbsent(emp) && !isMarkedAbsentToday(emp) && (
                      <button
                        onClick={(e) => handleMarkAbsent(emp, e)}
                        disabled={absentingId === (emp.employee_id || emp.id)}
                        style={{
                          padding: "4px 10px", borderRadius: 20, fontSize: 10.5, fontWeight: 700,
                          background: TOKENS.dangerBg, color: TOKENS.danger,
                          border: `1px solid ${TOKENS.danger}33`, cursor: "pointer",
                          display: "inline-flex", alignItems: "center", gap: 4,
                        }}
                      >
                        <UserX size={11} /> {absentingId === (emp.employee_id || emp.id) ? "Saving…" : "Mark Absent"}
                      </button>
                    )}
                    <div style={{
                      padding: "4px 12px", borderRadius: 20, fontSize: 11, fontWeight: 700,
                      background: cfg.bg, color: cfg.color, flexShrink: 0,
                    }}>
                      {cfg.label}
                    </div>
                  </div>
                  <ChevronRight size={16} color={TOKENS.border} style={{ flexShrink: 0 }} />
                </div>

                {/* Today's timeline strip */}
                <div style={{ marginTop: 12, display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
                  {/* Check In */}
                  <div style={{
                    padding: "10px 12px", borderRadius: 12,
                    background: checkInRec ? "#E7F5EF" : "#F8FAFC",
                    border: `1px solid ${checkInRec ? TOKENS.success + "44" : TOKENS.border}`,
                  }}>
                    <div style={{ fontSize: 10, fontWeight: 700, color: TOKENS.success, marginBottom: 3, textTransform: "uppercase" }}>
                      ↗ Check In
                    </div>
                    {checkInRec ? (
                      <>
                        <div style={{ fontSize: 15, fontWeight: 800, color: TOKENS.navyDeep }}>{checkInRec.timestamp}</div>
                        <div style={{ fontSize: 10.5, color: TOKENS.muted, marginTop: 2 }}>{checkInRec.city || "—"}</div>
                        {checkInRec.lat && (
                          <a href={`https://www.google.com/maps?q=${checkInRec.lat},${checkInRec.lng}`} target="_blank" rel="noopener noreferrer"
                            onClick={e => e.stopPropagation()}
                            style={{ fontSize: 10, fontWeight: 700, color: TOKENS.blue, textDecoration: "none", display: "inline-block", marginTop: 4 }}>
                            🗺️ View Map
                          </a>
                        )}
                      </>
                    ) : (
                      <div style={{ fontSize: 12, color: TOKENS.muted, fontStyle: "italic" }}>Not checked in</div>
                    )}
                  </div>

                  {/* Check Out */}
                  <div style={{
                    padding: "10px 12px", borderRadius: 12,
                    background: checkOutRec ? "#FEF3E2" : "#F8FAFC",
                    border: `1px solid ${checkOutRec ? TOKENS.warning + "44" : TOKENS.border}`,
                  }}>
                    <div style={{ fontSize: 10, fontWeight: 700, color: TOKENS.warning, marginBottom: 3, textTransform: "uppercase" }}>
                      ↙ Check Out
                    </div>
                    {checkOutRec ? (
                      <>
                        <div style={{ fontSize: 15, fontWeight: 800, color: TOKENS.navyDeep }}>{checkOutRec.timestamp}</div>
                        <div style={{ fontSize: 10.5, color: TOKENS.muted, marginTop: 2 }}>{checkOutRec.city || "—"}</div>
                        {checkOutRec.lat && (
                          <a href={`https://www.google.com/maps?q=${checkOutRec.lat},${checkOutRec.lng}`} target="_blank" rel="noopener noreferrer"
                            onClick={e => e.stopPropagation()}
                            style={{ fontSize: 10, fontWeight: 700, color: TOKENS.blue, textDecoration: "none", display: "inline-block", marginTop: 4 }}>
                            🗺️ View Map
                          </a>
                        )}
                      </>
                    ) : (
                      <div style={{ fontSize: 12, color: TOKENS.muted, fontStyle: "italic" }}>
                        {emp.checkedIn ? "On duty" : "Not checked out"}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Assign Task Modal overlay */}
      {isAssignOpen && (
        <div style={{
          position: "fixed", inset: 0,
          background: "rgba(10,21,45,0.45)",
          backdropFilter: "blur(6px)",
          display: "flex", alignItems: "flex-start", paddingTop: 80, justifyContent: "center",
          zIndex: 9999, padding: 16,
        }}>
          <form onSubmit={handleAssignTask} style={{
            background: "#fff", borderRadius: 20,
            width: "100%", maxWidth: 440, padding: 24,
            boxShadow: "0 24px 60px rgba(10,25,50,0.22)",
            border: `1.5px solid ${TOKENS.border}`,
          }}>
            <h3 style={{ fontFamily: "Fraunces, serif", fontSize: 18, fontWeight: 700, color: TOKENS.navyDeep, margin: "0 0 16px" }}>
              Assign Task to Agent
            </h3>

            {/* Select Employee */}
            <label style={{ fontSize: 11, fontWeight: 700, color: TOKENS.muted, letterSpacing: 0.5, display: "block", marginBottom: 6 }}>
              SELECT FIELD AGENT
            </label>
            <select
              value={selectedEmpId}
              onChange={(e) => setSelectedEmpId(e.target.value)}
              style={{
                width: "100%", padding: "10px 12px", borderRadius: 10,
                border: `1.5px solid ${TOKENS.border}`, fontSize: 13.5,
                background: TOKENS.cream, outline: "none", color: TOKENS.ink,
                marginBottom: 16,
              }}
            >
              {team.map((emp) => (
                <option key={emp.employee_id} value={emp.employee_id}>
                  {emp.name} ({emp.employee_id})
                </option>
              ))}
            </select>

            {/* Task Title */}
            <label style={{ fontSize: 11, fontWeight: 700, color: TOKENS.muted, letterSpacing: 0.5, display: "block", marginBottom: 6 }}>
              TASK TITLE / DESCRIPTION
            </label>
            <input
              placeholder="e.g. Calibrate XRF Spectrometer"
              value={taskTitle}
              onChange={(e) => setTaskTitle(e.target.value)}
              style={{
                width: "100%", padding: "10px 12px", borderRadius: 10,
                border: `1.5px solid ${TOKENS.border}`, fontSize: 13.5,
                background: TOKENS.cream, outline: "none", color: TOKENS.ink,
                marginBottom: 16,
              }}
            />

            {/* Category Dropdown */}
            <label style={{ fontSize: 11, fontWeight: 700, color: TOKENS.muted, letterSpacing: 0.5, display: "block", marginBottom: 6 }}>
              CATEGORY
            </label>
            <select
              value={taskCategory}
              onChange={(e) => setTaskCategory(e.target.value)}
              style={{
                width: "100%", padding: "10px 12px", borderRadius: 10,
                border: `1.5px solid ${TOKENS.border}`, fontSize: 13.5,
                background: TOKENS.cream, outline: "none", color: TOKENS.ink,
                marginBottom: 16,
              }}
            >
              <option value="Calibration">Calibration</option>
              <option value="Site Visit">Site Visit</option>
              <option value="MoU Pickup">MoU Pickup</option>
              <option value="Client Meeting">Client Meeting</option>
            </select>

            {/* Location */}
            <label style={{ fontSize: 11, fontWeight: 700, color: TOKENS.muted, letterSpacing: 0.5, display: "block", marginBottom: 6 }}>
              TARGET LOCATION
            </label>
            <input
              placeholder="e.g. Western Vault, Mumbai"
              value={taskLocation}
              onChange={(e) => setTaskLocation(e.target.value)}
              style={{
                width: "100%", padding: "10px 12px", borderRadius: 10,
                border: `1.5px solid ${TOKENS.border}`, fontSize: 13.5,
                background: TOKENS.cream, outline: "none", color: TOKENS.ink,
                marginBottom: 16,
              }}
            />

            {/* Client Ref */}
            <label style={{ fontSize: 11, fontWeight: 700, color: TOKENS.muted, letterSpacing: 0.5, display: "block", marginBottom: 6 }}>
              CLIENT REFERENCE ID
            </label>
            <input
              value={clientRef}
              onChange={(e) => setClientRef(e.target.value)}
              style={{
                width: "100%", padding: "10px 12px", borderRadius: 10,
                border: `1.5px solid ${TOKENS.border}`, fontSize: 13.5,
                background: TOKENS.cream, outline: "none", color: TOKENS.ink,
                marginBottom: 16,
              }}
            />

            {/* Client / Project Name */}
            <label style={{ fontSize: 11, fontWeight: 700, color: TOKENS.muted, letterSpacing: 0.5, display: "block", marginBottom: 6 }}>
              CLIENT / PROJECT NAME
            </label>
            <input
              placeholder="Client or Project Name"
              value={taskClientName}
              onChange={(e) => {
                setTaskClientName(e.target.value);
                setTaskProjectName(e.target.value);
              }}
              style={{
                width: "100%", padding: "10px 12px", borderRadius: 10,
                border: `1.5px solid ${TOKENS.border}`, fontSize: 13,
                background: TOKENS.cream, outline: "none", color: TOKENS.ink,
                marginBottom: 16,
              }}
            />

            {errorMsg && (
              <div style={{ color: TOKENS.danger, fontSize: 12, fontWeight: 600, marginBottom: 14 }}>
                ⚠️ {errorMsg}
              </div>
            )}

            <div style={{ display: "flex", gap: 10 }}>
              <button
                type="button"
                onClick={() => setIsAssignOpen(false)}
                style={{
                  flex: 1, padding: "10px 14px", borderRadius: 10,
                  border: `1.5px solid ${TOKENS.border}`, background: "#fff",
                  color: TOKENS.ink, fontWeight: 700, fontSize: 13, cursor: "pointer",
                }}
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={loading}
                style={{
                  flex: 1, padding: "10px 14px", borderRadius: 10,
                  border: "none", background: TOKENS.navyDeep,
                  color: "#fff", fontWeight: 700, fontSize: 13, cursor: "pointer",
                }}
              >
                {loading ? "Assigning..." : "Assign Task"}
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Add Employee Modal overlay */}
      {isAddEmpOpen && (
        <div style={{
          position: "fixed", inset: 0,
          background: "rgba(10,21,45,0.45)",
          backdropFilter: "blur(6px)",
          display: "flex", alignItems: "flex-start", paddingTop: 80, justifyContent: "center",
          zIndex: 9999, padding: 16,
        }}>
          <form onSubmit={handleAddEmployee} style={{
            background: "#fff", borderRadius: 20,
            width: "100%", maxWidth: 480, maxHeight: "90vh", display: "flex", flexDirection: "column",
            boxShadow: "0 24px 60px rgba(10,25,50,0.22)",
            border: `1.5px solid ${TOKENS.border}`, overflow: "hidden",
          }} className="screen-enter">
            <div style={{ padding: "20px 24px 14px", borderBottom: `1px solid ${TOKENS.border}` }}>
              <h3 style={{ fontFamily: "Fraunces, serif", fontSize: 18, fontWeight: 700, color: TOKENS.navyDeep, margin: 0 }}>
                Add New Employee Profile
              </h3>
              <p style={{ fontSize: 11.5, color: TOKENS.muted, margin: "4px 0 0" }}>
                Fill out the employee's login credentials, personal info, and emergency contact.
              </p>
            </div>

            <div style={{ padding: 24, overflowY: "auto", flex: 1 }}>
              {/* SECTION 1: Credentials & Job */}
              <div style={{ fontSize: 12, fontWeight: 700, color: TOKENS.gold, textTransform: "uppercase", letterSpacing: 0.5, marginBottom: 12 }}>
                👤 Basic & Login Credentials
              </div>

              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
                <div>
                  <label style={{ fontSize: 11, fontWeight: 700, color: TOKENS.muted, letterSpacing: 0.5, display: "block", marginBottom: 4 }}>
                    EMPLOYEE ID / USERNAME *
                  </label>
                  <input
                    placeholder="Enter Employee ID"
                    value={newEmpId}
                    onChange={(e) => setNewEmpId(e.target.value)}
                    style={{
                      width: "100%", padding: "9px 11px", borderRadius: 10,
                      border: `1.5px solid ${TOKENS.border}`, fontSize: 13,
                      background: TOKENS.cream, outline: "none", color: TOKENS.ink, marginBottom: 12,
                    }}
                  />
                </div>
                <div>
                  <label style={{ fontSize: 11, fontWeight: 700, color: TOKENS.muted, letterSpacing: 0.5, display: "block", marginBottom: 4 }}>
                    PASSWORD *
                  </label>
                  <input
                    type="text"
                    placeholder="e.g. agent123"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    style={{
                      width: "100%", padding: "9px 11px", borderRadius: 10,
                      border: `1.5px solid ${TOKENS.border}`, fontSize: 13,
                      background: TOKENS.cream, outline: "none", color: TOKENS.ink, marginBottom: 12,
                    }}
                  />
                </div>
              </div>

              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
                <div>
                  <label style={{ fontSize: 11, fontWeight: 700, color: TOKENS.muted, letterSpacing: 0.5, display: "block", marginBottom: 4 }}>
                    FULL NAME *
                  </label>
                  <input
                    placeholder="e.g. Arjun Mehta"
                    value={newName}
                    onChange={(e) => setNewName(e.target.value)}
                    style={{
                      width: "100%", padding: "9px 11px", borderRadius: 10,
                      border: `1.5px solid ${TOKENS.border}`, fontSize: 13,
                      background: TOKENS.cream, outline: "none", color: TOKENS.ink, marginBottom: 12,
                    }}
                  />
                </div>
                <div>
                  <label style={{ fontSize: 11, fontWeight: 700, color: TOKENS.muted, letterSpacing: 0.5, display: "block", marginBottom: 4 }}>
                    JOB ROLE / DESIGNATION *
                  </label>
                  <input
                    placeholder="e.g. Field Agent"
                    value={newRole}
                    onChange={(e) => setNewRole(e.target.value)}
                    style={{
                      width: "100%", padding: "9px 11px", borderRadius: 10,
                      border: `1.5px solid ${TOKENS.border}`, fontSize: 13,
                      background: TOKENS.cream, outline: "none", color: TOKENS.ink, marginBottom: 12,
                    }}
                  />
                </div>
              </div>

              {/* SECTION 2: Personal Details */}
              <div style={{ fontSize: 12, fontWeight: 700, color: TOKENS.gold, textTransform: "uppercase", letterSpacing: 0.5, margin: "12px 0 12px" }}>
                📋 Personal Details
              </div>

              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
                <div>
                  <label style={{ fontSize: 11, fontWeight: 700, color: TOKENS.muted, letterSpacing: 0.5, display: "block", marginBottom: 4 }}>
                    FATHER'S NAME
                  </label>
                  <input
                    placeholder="Father's name"
                    value={newFatherName}
                    onChange={(e) => setNewFatherName(e.target.value)}
                    style={{
                      width: "100%", padding: "9px 11px", borderRadius: 10,
                      border: `1.5px solid ${TOKENS.border}`, fontSize: 13,
                      background: TOKENS.cream, outline: "none", color: TOKENS.ink, marginBottom: 12,
                    }}
                  />
                </div>
                <div>
                  <label style={{ fontSize: 11, fontWeight: 700, color: TOKENS.muted, letterSpacing: 0.5, display: "block", marginBottom: 4 }}>
                    MOTHER'S NAME
                  </label>
                  <input
                    placeholder="Mother's name"
                    value={newMotherName}
                    onChange={(e) => setNewMotherName(e.target.value)}
                    style={{
                      width: "100%", padding: "9px 11px", borderRadius: 10,
                      border: `1.5px solid ${TOKENS.border}`, fontSize: 13,
                      background: TOKENS.cream, outline: "none", color: TOKENS.ink, marginBottom: 12,
                    }}
                  />
                </div>
              </div>

              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
                <div>
                  <label style={{ fontSize: 11, fontWeight: 700, color: TOKENS.muted, letterSpacing: 0.5, display: "block", marginBottom: 4 }}>
                    DATE OF BIRTH
                  </label>
                  <input
                    type="date"
                    value={newDob}
                    onChange={(e) => setNewDob(e.target.value)}
                    style={{
                      width: "100%", padding: "9px 11px", borderRadius: 10,
                      border: `1.5px solid ${TOKENS.border}`, fontSize: 13,
                      background: TOKENS.cream, outline: "none", color: TOKENS.ink, marginBottom: 12,
                    }}
                  />
                </div>
                <div>
                  <label style={{ fontSize: 11, fontWeight: 700, color: TOKENS.muted, letterSpacing: 0.5, display: "block", marginBottom: 4 }}>
                    BLOOD GROUP
                  </label>
                  <select
                    value={newBloodGroup}
                    onChange={(e) => setNewBloodGroup(e.target.value)}
                    style={{
                      width: "100%", padding: "9px 11px", borderRadius: 10,
                      border: `1.5px solid ${TOKENS.border}`, fontSize: 13,
                      background: TOKENS.cream, outline: "none", color: TOKENS.ink, marginBottom: 12,
                    }}
                  >
                    {["A+", "B+", "O+", "AB+", "A-", "B-", "O-", "AB-", "A1+", "A1-", "A1B+", "A1B-"].map(bg => (
                      <option key={bg} value={bg}>{bg}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 12 }}>
                <WhatsAppPhoneField
                  label="PHONE / WHATSAPP"
                  value={newPhone}
                  onChange={setNewPhone}
                  onWhatsApp={setNewHasWhatsapp}
                  onStatus={setWaCheckStatus}
                />
                <div>
                  <label style={{ fontSize: 11, fontWeight: 700, color: TOKENS.muted, letterSpacing: 0.5, display: "block", marginBottom: 4 }}>
                    EMAIL ADDRESS
                  </label>
                  <input
                    type="email"
                    placeholder="agent@goldpe.in"
                    value={newEmail}
                    onChange={(e) => setNewEmail(e.target.value)}
                    style={{
                      width: "100%", padding: "9px 11px", borderRadius: 10,
                      border: `1.5px solid ${TOKENS.border}`, fontSize: 13,
                      background: TOKENS.cream, outline: "none", color: TOKENS.ink,
                    }}
                  />
                </div>
              </div>

              {/* SECTION 3: Emergency Contact */}
              <div style={{ fontSize: 12, fontWeight: 700, color: TOKENS.gold, textTransform: "uppercase", letterSpacing: 0.5, margin: "12px 0 12px" }}>
                🚨 Emergency Contact
              </div>

              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 8 }}>
                <div>
                  <label style={{ fontSize: 10.5, fontWeight: 700, color: TOKENS.muted, letterSpacing: 0.4, display: "block", marginBottom: 4 }}>
                    CONTACT NAME
                  </label>
                  <input
                    placeholder="Full Name"
                    value={newEmergencyName}
                    onChange={(e) => setNewEmergencyName(e.target.value)}
                    style={{
                      width: "100%", padding: "9px 8px", borderRadius: 10,
                      border: `1.5px solid ${TOKENS.border}`, fontSize: 12.5,
                      background: TOKENS.cream, outline: "none", color: TOKENS.ink, marginBottom: 12,
                    }}
                  />
                </div>
                <div>
                  <label style={{ fontSize: 10.5, fontWeight: 700, color: TOKENS.muted, letterSpacing: 0.4, display: "block", marginBottom: 4 }}>
                    RELATION
                  </label>
                  <input
                    placeholder="e.g. Father"
                    value={newEmergencyRelationship}
                    onChange={(e) => setNewEmergencyRelationship(e.target.value)}
                    style={{
                      width: "100%", padding: "9px 8px", borderRadius: 10,
                      border: `1.5px solid ${TOKENS.border}`, fontSize: 12.5,
                      background: TOKENS.cream, outline: "none", color: TOKENS.ink, marginBottom: 12,
                    }}
                  />
                </div>
                <div>
                  <label style={{ fontSize: 10.5, fontWeight: 700, color: TOKENS.muted, letterSpacing: 0.4, display: "block", marginBottom: 4 }}>
                    PHONE
                  </label>
                  <input
                    placeholder="Contact Phone"
                    value={newEmergencyPhone}
                    onChange={(e) => setNewEmergencyPhone(e.target.value)}
                    style={{
                      width: "100%", padding: "9px 8px", borderRadius: 10,
                      border: `1.5px solid ${TOKENS.border}`, fontSize: 12.5,
                      background: TOKENS.cream, outline: "none", color: TOKENS.ink, marginBottom: 12,
                    }}
                  />
                </div>
              </div>

              {/* SECTION 4: Organization Details */}
              <div style={{ fontSize: 12, fontWeight: 700, color: TOKENS.gold, textTransform: "uppercase", letterSpacing: 0.5, margin: "12px 0 12px" }}>
                🏢 Organization Details
              </div>

              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 8 }}>
                <div>
                  <label style={{ fontSize: 10.5, fontWeight: 700, color: TOKENS.muted, letterSpacing: 0.4, display: "block", marginBottom: 4 }}>
                    CLIENT NAME
                  </label>
                  <input
                    placeholder="Client Name"
                    value={newClient}
                    onChange={(e) => setNewClient(e.target.value)}
                    style={{
                      width: "100%", padding: "9px 8px", borderRadius: 10,
                      border: `1.5px solid ${TOKENS.border}`, fontSize: 12.5,
                      background: TOKENS.cream, outline: "none", color: TOKENS.ink, marginBottom: 12,
                    }}
                  />
                </div>
                <div>
                  <label style={{ fontSize: 10.5, fontWeight: 700, color: TOKENS.muted, letterSpacing: 0.4, display: "block", marginBottom: 4 }}>
                    ASSIGNED TEAM
                  </label>
                  <input
                    placeholder="Assigned Team"
                    value={newTeam}
                    onChange={(e) => setNewTeam(e.target.value)}
                    style={{
                      width: "100%", padding: "9px 8px", borderRadius: 10,
                      border: `1.5px solid ${TOKENS.border}`, fontSize: 12.5,
                      background: TOKENS.cream, outline: "none", color: TOKENS.ink, marginBottom: 12,
                    }}
                  />
                </div>
                <div>
                  <label style={{ fontSize: 10.5, fontWeight: 700, color: TOKENS.muted, letterSpacing: 0.4, display: "block", marginBottom: 4 }}>
                    JOINING DATE
                  </label>
                  <input
                    type="date"
                    value={newJoiningDate}
                    onChange={(e) => setNewJoiningDate(e.target.value)}
                    style={{
                      width: "100%", padding: "9px 8px", borderRadius: 10,
                      border: `1.5px solid ${TOKENS.border}`, fontSize: 12.5,
                      background: TOKENS.cream, outline: "none", color: TOKENS.ink, marginBottom: 12,
                    }}
                  />
                </div>
              </div>

              {errorAdd && (
                <div style={{ color: TOKENS.danger, fontSize: 12, fontWeight: 600, marginTop: 8 }}>
                  ⚠️ {errorAdd}
                </div>
              )}
            </div>

            <div style={{ padding: "14px 24px", borderTop: `1px solid ${TOKENS.border}`, display: "flex", gap: 10, background: "#FAFBFD" }}>
              <button
                type="button"
                onClick={() => setIsAddEmpOpen(false)}
                style={{
                  flex: 1, padding: "10px 14px", borderRadius: 10,
                  border: `1.5px solid ${TOKENS.border}`, background: "#fff",
                  color: TOKENS.ink, fontWeight: 700, fontSize: 13, cursor: "pointer",
                }}
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={loadingAdd}
                style={{
                  flex: 1, padding: "10px 14px", borderRadius: 10,
                  border: "none", background: TOKENS.navyDeep,
                  color: "#fff", fontWeight: 700, fontSize: 13, cursor: "pointer",
                }}
              >
                {loadingAdd ? "Creating..." : "Create Profile"}
              </button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}
